import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  Container,
  Alert,
  IconButton,
  Snackbar,
  useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import authStore from '../stores/AuthStore';
import Logo from '../assets/images/full-logo-white.png';

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState(null);
  const [submissionError, setSubmissionError] = useState(null);
  const [sent, setSent] = useState(false);
  const theme = useTheme();

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address.');
      return;
    }
    setError(null);
    try {
      await authStore.forgotPassword(email);
      // Always show the same confirmation (don't reveal whether the account exists).
      setSent(true);
    } catch (err) {
      setSent(true);
    }
  };

  return (
    <Box component="main" sx={{
      backgroundImage: `url(${require('../assets/images/physics.png')})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      height: '100vh',
      width: '100vw',
      maxWidth: '100vw',
      overflowX: 'hidden',
    }}>
      <Container maxWidth="sm" sx={{
        display: 'flex',
        justifyContent: 'start',
        alignItems: 'center',
        flexDirection: 'column',
        height: '100%',
      }}>
        <Box sx={{ m: 2 }}>
          <img src={Logo} alt="Ear Physics Logo" style={{ width: '200px', height: 'auto' }} />
        </Box>
        <Box
          sx={{
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            backdropFilter: 'blur(5px)',
            borderRadius: 2,
            p: 4,
            width: '100%',
            maxWidth: 'sm',
          }}
        >
          <Box sx={{ mb: 2, display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
            <IconButton color="inherit" component={Link} to="/sign-in">
              <ArrowBackIcon />
            </IconButton>
            <Typography
              component="h1"
              variant="h5"
              gutterBottom
              sx={{ color: theme.palette.text.primary, flex: 1, textAlign: 'center', mr: 4 }}
            >
              Forgot Password
            </Typography>
          </Box>

          {submissionError && (
            <Snackbar open={true} autoHideDuration={6000} onClose={() => setSubmissionError(null)}>
              <Alert severity="error" onClose={() => setSubmissionError(null)}>{submissionError}</Alert>
            </Snackbar>
          )}

          {sent ? (
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" sx={{ color: theme.palette.text.primary }}>
                Check your email
              </Typography>
              <Typography variant="body1" sx={{ mt: 1, color: theme.palette.text.secondary }}>
                If an account exists for {email}, we've sent a link to reset your password.
                Check your inbox and spam folder.
              </Typography>
              <Button
                fullWidth
                variant="text"
                component={Link}
                to="/sign-in"
                sx={{ mt: 2, color: theme.palette.text.primary }}
              >
                Back to Sign In
              </Button>
            </Box>
          ) : (
            <>
              <Typography component="p" variant="body1" align="center" sx={{ mb: 2, color: theme.palette.text.secondary }}>
                Enter your email and we'll send you a reset link.
              </Typography>
              <Box component="form" noValidate onSubmit={handleSubmit} sx={{ mt: 1, width: '100%' }}>
                <Typography variant="body2" sx={{ color: theme.palette.text.primary, mb: 1 }}>
                  Email
                </Typography>
                <TextField
                  margin="dense"
                  required
                  fullWidth
                  id="email"
                  name="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  error={Boolean(error)}
                  placeholder="Enter your email"
                  helperText={error}
                  InputProps={{
                    style: {
                      backgroundColor: theme.palette.background.paper,
                      color: theme.palette.text.primary,
                    },
                  }}
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="outlined"
                  sx={{
                    mt: 3,
                    mb: 2,
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                    '&:hover': { backgroundColor: theme.palette.primary.dark },
                  }}
                >
                  Send Reset Link
                </Button>
                <Button
                  fullWidth
                  variant="text"
                  component={Link}
                  to="/sign-in"
                  sx={{ mt: 1, color: theme.palette.text.primary }}
                >
                  Back to Sign In
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default ForgotPasswordPage;
